import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
	DeviceCategory,
	UnpairedVelavuDevice,
	VelavuAPI,
	VelavuAsset,
	VelavuDevice,
} from "velavu-js-api";
import UnpairAssetTag from "../../../data/unpair-asset-tag";
import IconLink from "../../../dynamicicons/icon-link";
import CategorizationTag from "../../../elements/categorization-tag";
import StatusIndicator from "../../../elements/status-indicator";
import VelavuItemIcon from "../../../elements/velavu-item-icon";
import * as styles from "./asset-link.module.scss";
import LinkCard from "./link-card";
import whitelabel from "../../../whitelabel";
import ConnectionStatus from "../../../data/connection-status";
import { mapDeviceModel } from "../../../helper/language-helper";
import VelavuAssetItemIcon from "../../../elements/velavu-asset-item-icon";
import { useHasPermission } from "../../sign-in-context/sign-in-context";

interface AssetLinkProps {
	asset?: VelavuAsset;
	device?: VelavuDevice;
	deviceOnly?: boolean;
	onUnpair: (type: UnpairAssetTag) => void;
	onPair: (data: UnpairAssetTag) => void;

	defaultOpenPair?: undefined | "asset" | "tag";

	onClickAssetName?: () => void;
}

//The maximum number of search suggestions to fetch from the backend
const SUGGESTION_LIMIT = 8;

export default function AssetLink(props: AssetLinkProps) {
	const { t } = useTranslation();
	const pairLinked = props.asset && props.device;

	const isEditable = useHasPermission("assets:write");

	const fetchAssets = useCallback(async (filter: string | undefined) => {
		const response = await VelavuAPI.assets.getAllAssets(
			{
				//We only want unpaired assets
				paired: false,
				//Filter by name
				name: filter,
			},
			{ limit: SUGGESTION_LIMIT },
		);

		return response.data;
	}, []);

	const fetchTags = useCallback(async (filter: string | undefined) => {
		const response = await VelavuAPI.devices.getAllDevices(
			{
				//We only want tags
				category: DeviceCategory.Tag,
				//We only want unpaired devices
				paired: false,
				//Filter by ID
				id: filter,
			},
			{ limit: SUGGESTION_LIMIT },
		);

		return response.data;
	}, []);

	return (
		<div className={styles.container}>
			{!props.deviceOnly && (
				<LinkCard
					title={t("asset_detail.link.asset")}
					disableUnpair={!pairLinked || !isEditable}
					disablePair={!isEditable}
					onPair={(asset) =>
						props.onPair({
							type: "asset",
							asset: asset,
							tag: props.device!,
						})
					}
					onUnpair={() =>
						props.onUnpair({
							type: "asset",
							asset: props.asset!,
							tag: props.device!,
						})
					}
					onClickName={props.onClickAssetName}
					linkDetails={
						props.asset
							? {
									icon: (
										<VelavuAssetItemIcon
											asset={props.asset}
											status={
												props.device
													? props.device.online
														? "active"
														: "inactive"
													: "unpaired"
											}
										/>
									),
									name: props.asset.name,
									subtitle: (
										<CategorizationTag
											categorization={{
												category: props.asset.category,
												group: props.asset.group,
											}}
										/>
									),
								}
							: undefined
					}
					blankLabel={t("asset_detail.link.no_asset")}
					unpairLabel={t("asset_detail.link.unpair_asset")}
					fetchData={fetchAssets}
					mapDataID={(asset) => asset.id}
					renderData={(asset) => (
						<div className={styles.assetAutocompleteItem}>
							<span className={styles.assetAutocompleteTitle}>
								{asset.name}
							</span>
							<CategorizationTag
								categorization={{
									category: asset.category,
									group: asset.group,
								}}
							/>
						</div>
					)}
					addData={{
						label: t("asset_detail.link.new_asset"),
						link: "/assets/create",
					}}
					defaultOpen={props.defaultOpenPair === "asset"}
				/>
			)}

			{!props.deviceOnly && (
				<div className={styles.linkIndicator}>
					<IconLink color={whitelabel.palette.secondary500} />
				</div>
			)}

			<LinkCard
				title={t("asset_detail.link.device")}
				disableUnpair={!pairLinked || !isEditable}
				hideUnpair={props.device?.category === DeviceCategory.Anchor}
				disablePair={!isEditable}
				onPair={(tag) =>
					props.onPair({ type: "tag", asset: props.asset!, tag: tag })
				}
				onUnpair={() =>
					props.onUnpair({
						type: "tag",
						asset: props.asset!,
						tag: props.device!,
					})
				}
				linkDetails={
					props.device
						? {
								icon: (
									<VelavuItemIcon
										data={{ model: props.device.model }}
									/>
								),
								name: props.device.id,
								subtitle: mapDeviceModel(props.device.model, t),
							}
						: undefined
				}
				blankLabel={t("asset_detail.link.no_device")}
				unpairLabel={t("asset_detail.link.unpair_device")}
				fetchData={fetchTags}
				mapDataID={(tag) => tag.id}
				renderData={(tag) => (
					<TagAutocompleteRow key={tag.id} tag={tag} />
				)}
				defaultOpen={props.defaultOpenPair === "tag"}
			/>
		</div>
	);
}

function TagAutocompleteRow(props: { tag: UnpairedVelavuDevice }) {
	const { t } = useTranslation();
	const tag = props.tag;

	let status: ConnectionStatus;
	switch (tag.online) {
		case true:
			status = "active";
			break;
		case false:
			status = "inactive";
			break;
		default:
			status = "registered";
			break;
	}

	return (
		<div className={styles.tagAutocompleteItem}>
			<div className={styles.tagAutocompleteText}>
				<span className={styles.tagAutocompleteTitle}>{tag.id}</span>
				<span className={styles.tagAutocompleteSubtitle}>
					{mapDeviceModel(tag.model, t)}
				</span>
			</div>
			<StatusIndicator status={status} />
		</div>
	);
}
